/**
 * Plan download workflow — pre-cache map tiles for an extent derived from
 * a plan's features.
 *
 * The workflow:
 *   1. Load the plan's features and compute a bounding box (Turf).
 *   2. Snap that bbox to the lowest zoom whose tile grid is ≤ 2×2
 *      (`findFloorZoom`). That "floor zoom" is the coarsest level we
 *      download — every zoom from there up to the layer's max native zoom
 *      (capped at 16) is enumerated and pinned.
 *   3. Show a live estimate of tile count + storage, run a pre-flight
 *      `canFit` check, then call `tileCacheService.pinTiles` per layer.
 *
 * This component is deliberately self-contained: it renders its own
 * MapLibre instance with the selected base layer so the cairn-map surface
 * is not co-opted for a single workflow. Drag-to-resize handles on the
 * selection box are a follow-up; the v1 extent is the auto-computed
 * feature bbox (the "fixed image" snapped outline is always shown).
 */
import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref, type Ref } from 'lit/directives/ref.js'
import maplibregl from 'maplibre-gl'
import maplibreCssText from 'maplibre-gl/dist/maplibre-gl.css?inline'
import { featureCollection, feature as turfFeature, bbox } from '@turf/turf'

import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/iconbutton/icon-button.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'
import '@material/web/progress/linear-progress.js'
import '@material/web/checkbox/checkbox.js'

import { router } from '../../utils/router.js'
import { db } from '../../database/schema.js'
import { featureStorage } from '../../storage/feature-storage.js'
import { loadLayerPreferences } from '../../services/layer-preferences-storage.js'
import { baseLayers, type BaseLayer, type OverlayLayer } from '../../lib/layer-configs.js'
import { bboxToTileRange, findFloorZoom } from '../../lib/tile-math.js'
import {
  cairnifyTileUrl,
  cairnProtocolHandler,
  substituteTileTemplate,
} from '../../services/tile-cache/cairn-protocol.js'
import { tileCacheService } from '../../services/tile-cache/tile-cache-service.js'
import { vectorStyleCacheService } from '../../services/tile-cache/vector-style-cache-service.js'
import type { TileCoord } from '../../services/tile-cache/tile-cache-types.js'
import { logger } from '../../utils/logger.js'
import type { Feature } from '../../schemas/feature.js'
import { DEM_URL } from '../../services/terrain/dem-config.js'
import { mapStorage } from '../../storage/index.js'
import { emergencyDataService } from '../../services/emergency/emergency-data-service.js'
import type { EmergencyDataType } from '../../services/emergency/emergency-types.js'
import type { LayerPreferences } from '../../services/layer-preferences-storage.js'
import { PMTiles } from 'pmtiles'
import { Capacitor } from '@capacitor/core'

type TerrainQuality = 'standard' | 'detailed' | 'maximum'

const TERRAIN_QUALITY_OPTIONS: {
  value: TerrainQuality
  label: string
  subtitle: string
  maxZoom: number
}[] = [
  { value: 'standard', label: 'Standard', subtitle: '~10m elevation data', maxZoom: 13 },
  {
    value: 'detailed',
    label: 'Detailed',
    subtitle: 'Up to ~2m, LiDAR where available',
    maxZoom: 15,
  },
  { value: 'maximum', label: 'Maximum', subtitle: 'Up to ~1m, LiDAR where available', maxZoom: 16 },
]

const log = logger.create('plan-download')

/** Per-layer zoom ceiling — prevents 4× blow-ups from deep zoom levels. */
const MAX_DOWNLOAD_ZOOM = 16

/** Per-type average bytes per tile for estimates. */
const BYTES_PER_TILE_RASTER = 15_000
const BYTES_PER_TILE_VECTOR_PBF = 8_000
const BYTES_PER_TILE_DEM = 35_000

const GRID_MARGIN = 4

interface LayerPlan {
  id: string
  name: string
  urlTemplate: string
  minZoom: number
  maxZoom: number
  tileCount: number
  bytesPerTile: number
}

type OverlayGroup = 'hazards' | 'land' | 'emergency' | 'wildlife' | 'comms'

interface DownloadableOverlay {
  id: string
  name: string
  group: OverlayGroup
  prefKeys: string[]
}

const OVERLAY_GROUP_LABELS: Record<OverlayGroup, string> = {
  hazards: 'Hazards',
  land: 'Land & Trails',
  emergency: 'Emergency',
  wildlife: 'Wildlife',
  comms: 'Communications',
}

const OVERLAY_GROUPS: OverlayGroup[] = ['hazards', 'land', 'emergency', 'wildlife', 'comms']

const DOWNLOADABLE_OVERLAYS: readonly DownloadableOverlay[] = [
  {
    id: 'avalanche-zones',
    name: 'Avalanche Zones',
    group: 'hazards',
    prefKeys: ['avalanche-zones'],
  },
  {
    id: 'fire-group',
    name: 'Fire & Closures',
    group: 'hazards',
    prefKeys: ['fire-wildfires', 'fire-firms', 'fire-restrictions'],
  },
  { id: 'blm-sma', name: 'Land Management (BLM SMA)', group: 'land', prefKeys: ['blm-sma'] },
  { id: 'padus', name: 'Protected Areas (PAD-US)', group: 'land', prefKeys: ['padus'] },
  { id: 'backcountry', name: 'Trails & POIs (OSM)', group: 'land', prefKeys: ['backcountry'] },
  {
    id: 'cotrex',
    name: 'COTREX (Colorado Trails)',
    group: 'land',
    prefKeys: ['cotrex', 'cotrex-closures'],
  },
  { id: 'climbing-crags', name: 'Climbs (OpenBeta)', group: 'land', prefKeys: ['climbing-crags'] },
  { id: 'plss', name: 'PLSS Survey Grid', group: 'land', prefKeys: ['plss'] },
  { id: 'emergency-group', name: 'Emergency Services', group: 'emergency', prefKeys: [] },
  {
    id: 'wildlife-inat',
    name: 'Wildlife Sightings',
    group: 'wildlife',
    prefKeys: ['wildlife-inat'],
  },
  {
    id: 'wildlife-critical-habitat',
    name: 'Critical Habitat (USFWS)',
    group: 'wildlife',
    prefKeys: ['wildlife-critical-habitat'],
  },
  {
    id: 'wildlife-nps-alerts',
    name: 'Park Alerts (NPS)',
    group: 'wildlife',
    prefKeys: ['wildlife-nps-alerts'],
  },
  { id: 'cellular', name: 'US Cellular Coverage', group: 'comms', prefKeys: ['cellular-carriers'] },
  { id: 'satellite', name: 'Satellite Positions', group: 'comms', prefKeys: ['satellite'] },
]

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''

interface PMTilesOverlayConfig {
  pmtilesUrl: string
  sourceId: string
  minZoom: number
  maxZoom: number
}

const PMTILES_OVERLAY_CONFIGS: Record<string, PMTilesOverlayConfig> = {
  'blm-sma': {
    pmtilesUrl: `${TILE_ORIGIN}/data/land/blm-sma.pmtiles`,
    sourceId: 'pmtiles-blm-sma',
    minZoom: 3,
    maxZoom: 14,
  },
  padus: {
    pmtilesUrl: `${TILE_ORIGIN}/data/land/padus-fee.pmtiles`,
    sourceId: 'pmtiles-padus',
    minZoom: 3,
    maxZoom: 14,
  },
  cotrex: {
    pmtilesUrl: `${TILE_ORIGIN}/data/cotrex/cotrex.pmtiles`,
    sourceId: 'pmtiles-cotrex',
    minZoom: 7,
    maxZoom: 14,
  },
  plss: {
    pmtilesUrl: `${TILE_ORIGIN}/data/land/plss.pmtiles`,
    sourceId: 'pmtiles-plss',
    minZoom: 7,
    maxZoom: 12,
  },
  'wildlife-critical-habitat': {
    pmtilesUrl: `${TILE_ORIGIN}/data/wildlife/critical-habitat.pmtiles`,
    sourceId: 'pmtiles-critical-habitat',
    minZoom: 3,
    maxZoom: 14,
  },
}

let protocolRegistered = false
function ensureCairnProtocolRegistered(): void {
  if (protocolRegistered) return
  protocolRegistered = true
  maplibregl.addProtocol('cairn', cairnProtocolHandler)
  void tileCacheService.init()
}

@customElement('plan-download')
export class PlanDownload extends LitElement {
  static override styles = [
    unsafeCSS(maplibreCssText),
    css`
      :host {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        background: var(--cairn-background-color, #f8fafc);
      }

      .header {
        display: flex;
        align-items: center;
        padding: var(--cairn-spacing-md, 16px);
        background: var(--cairn-surface-color, #ffffff);
        border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
        flex-shrink: 0;
      }

      .header md-icon-button {
        margin-right: var(--cairn-spacing-sm, 8px);
        --md-icon-button-icon-size: 24px;
      }

      .header-title {
        font-size: var(--cairn-text-size-xl, 24px);
        font-weight: 600;
        color: var(--cairn-text-color, #1e293b);
        margin: 0;
      }

      .map-container {
        flex: 1;
        min-height: 0;
        position: relative;
      }

      .map-el {
        width: 100%;
        height: 100%;
      }

      .action-bar {
        background: var(--cairn-surface-color, #ffffff);
        border-top: 1px solid var(--cairn-border-color, #e2e8f0);
        padding: var(--cairn-spacing-md, 16px);
        flex-shrink: 0;
      }

      .select-row {
        margin-bottom: 12px;
      }

      md-outlined-select {
        width: 100%;
        --md-outlined-select-text-field-container-height: 48px;
        --md-outlined-select-text-field-container-shape: 8px;
      }

      .estimate {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        font-size: 14px;
        color: var(--cairn-text-color, #1e293b);
        margin-bottom: 12px;
      }

      .estimate-primary {
        font-size: 18px;
        font-weight: 600;
      }

      .estimate-secondary {
        color: var(--cairn-text-secondary-color, #64748b);
        font-size: 12px;
      }

      .layer-list {
        font-size: 12px;
        color: var(--cairn-text-secondary-color, #64748b);
        margin-bottom: 12px;
        max-height: 60px;
        overflow-y: auto;
      }

      .layer-row {
        display: flex;
        justify-content: space-between;
        padding: 2px 0;
      }

      .button-row {
        display: flex;
        gap: 8px;
      }

      .button-row md-filled-button,
      .button-row md-outlined-button {
        flex: 1;
      }

      .progress-section {
        margin-top: 8px;
      }

      .progress-label {
        font-size: 12px;
        color: var(--cairn-text-secondary-color, #64748b);
        margin-bottom: 8px;
      }

      md-linear-progress {
        width: 100%;
        --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
        --md-linear-progress-track-color: var(--cairn-border-color, #e2e8f0);
        --md-linear-progress-track-height: 4px;
        --md-linear-progress-active-indicator-height: 4px;
        margin-bottom: 12px;
      }

      .error {
        color: var(--cairn-error-color, #dc2626);
        font-size: 13px;
        margin-top: 8px;
      }

      .material-icons {
        font-family: 'Material Symbols Outlined';
        font-weight: normal;
        font-style: normal;
        font-size: 24px;
        line-height: 1;
      }

      .overlay-section {
        margin-bottom: 12px;
      }

      .overlay-dropdown {
        position: relative;
      }

      .overlay-trigger {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border: 1px solid var(--cairn-border-color, #e2e8f0);
        border-radius: 8px;
        background: var(--cairn-surface-color, #ffffff);
        cursor: pointer;
        font-size: 14px;
        color: var(--cairn-text-color, #1e293b);
      }

      .overlay-trigger:hover {
        border-color: var(--cairn-text-secondary-color, #94a3b8);
      }

      .overlay-trigger .material-icons {
        font-size: 20px;
        color: var(--cairn-text-secondary-color, #64748b);
        transition: transform 0.2s;
      }

      .overlay-trigger[aria-expanded='true'] .material-icons {
        transform: rotate(180deg);
      }

      .overlay-menu {
        position: absolute;
        bottom: calc(100% + 4px);
        left: 0;
        right: 0;
        z-index: 10;
        background: var(--cairn-surface-color, #ffffff);
        border: 1px solid var(--cairn-border-color, #e2e8f0);
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        max-height: 240px;
        overflow-y: auto;
        padding: 8px 12px;
      }

      .overlay-group-name {
        font-size: 11px;
        font-weight: 600;
        color: var(--cairn-text-secondary-color, #64748b);
        padding: 6px 0 2px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .overlay-group-name:first-child {
        padding-top: 0;
      }

      .overlay-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 2px 0;
        cursor: pointer;
        font-size: 13px;
        color: var(--cairn-text-color, #1e293b);
      }

      .overlay-item md-checkbox {
        --md-checkbox-container-size: 18px;
      }

      .overlay-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 8px;
      }

      .overlay-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 16px;
        background: var(--cairn-primary-color, #3b82f6);
        color: #fff;
        font-size: 12px;
        white-space: nowrap;
      }

      .overlay-chip button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        color: inherit;
        cursor: pointer;
        padding: 0;
        margin-left: 2px;
        opacity: 0.8;
      }

      .overlay-chip button:hover {
        opacity: 1;
      }

      .overlay-chip .material-icons {
        font-size: 14px;
      }
    `,
  ]

  @property({ type: String }) planId?: string

  @state() private planName = ''
  @state() private userBbox: [number, number, number, number] | null = null
  @state() private snappedBbox: [number, number, number, number] | null = null
  @state() private layerPlans: LayerPlan[] = []
  @state() private totalTiles = 0
  @state() private estimatedBytes = 0
  @state() private isDownloading = false
  @state() private progressDone = 0
  @state() private progressTotal = 0
  @state() private currentLayerName = ''
  @state() private errorMessage = ''
  @state() private terrainQuality: TerrainQuality = 'standard'
  @state() private ttlDays = 30
  @state() private selectedOverlays = new Set<string>()
  @state() private overlayMenuOpen = false
  @state() private gridZoom = 0
  @state() private selectedGridTiles = new Set<string>()

  private mapRef: Ref<HTMLDivElement> = createRef()
  private map: maplibregl.Map | undefined
  private abortController?: AbortController

  override connectedCallback(): void {
    super.connectedCallback()
    ensureCairnProtocolRegistered()
    document.addEventListener('click', this.onDocumentClick)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener('click', this.onDocumentClick)
    this.abortController?.abort()
    this.map?.remove()
    this.map = undefined
  }

  protected override firstUpdated(): void {
    void this.load()
  }

  // ──────────────────────────────────────────────────────────────────
  // Load plan + features, compute bbox, build layer download plans
  // ──────────────────────────────────────────────────────────────────

  private async load(): Promise<void> {
    if (!this.planId) {
      this.errorMessage = 'No plan id provided'
      return
    }

    const plan = await db.plans.get(this.planId)
    if (!plan) {
      this.errorMessage = `Plan ${this.planId} not found`
      return
    }
    this.planName = plan.name

    const features = await featureStorage.getFeaturesForEntity('plan', this.planId)
    const bbox = this.computeFeaturesBbox(features)
    if (!bbox) {
      this.errorMessage = 'Plan has no features to derive a download extent from'
      return
    }
    this.userBbox = bbox
    this.gridZoom = findFloorZoom(bbox) + 1
    this.selectedGridTiles = this.computeCoreTiles(bbox)
    this.snappedBbox = this.selectedTilesToBbox()
    this.initSelectedOverlays(loadLayerPreferences())

    await this.buildLayerPlans()
    this.initMap()
  }

  /** Compute the union bbox of all feature geometries as [w, s, e, n]. */
  private computeFeaturesBbox(
    features: readonly Feature[]
  ): [number, number, number, number] | null {
    if (features.length === 0) return null
    try {
      const fc = featureCollection(features.map(f => turfFeature(f.geometry, { id: f.id })))
      const bb = bbox(fc) as [number, number, number, number]
      // Pad by ~10% so the extent is not pinned tight to feature geometry.
      const pad = 0.1
      const dx = (bb[2] - bb[0]) * pad
      const dy = (bb[3] - bb[1]) * pad
      return [bb[0] - dx, bb[1] - dy, bb[2] + dx, bb[3] + dy]
    } catch (err) {
      log.warn('failed to compute features bbox', err)
      return null
    }
  }

  private computeCoreTiles(bbox: [number, number, number, number]): Set<string> {
    const { minX, minY, maxX, maxY } = bboxToTileRange(bbox, this.gridZoom)
    const tiles = new Set<string>()
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        tiles.add(`${x},${y}`)
      }
    }
    return tiles
  }

  private selectedTilesToBbox(): [number, number, number, number] | null {
    if (this.selectedGridTiles.size === 0) return null
    let west = 180
    let south = 90
    let east = -180
    let north = -90
    for (const key of this.selectedGridTiles) {
      const [x, y] = key.split(',').map(Number)
      const [w, s, e, n] = tileToBounds(x, y, this.gridZoom)
      west = Math.min(west, w)
      south = Math.min(south, s)
      east = Math.max(east, e)
      north = Math.max(north, n)
    }
    return [west, south, east, north]
  }

  private buildGridGeoJSON(): GeoJSON.FeatureCollection {
    if (!this.userBbox) return { type: 'FeatureCollection', features: [] }
    const { minX, minY, maxX, maxY } = bboxToTileRange(this.userBbox, this.gridZoom)
    const features: GeoJSON.Feature[] = []
    for (let x = minX - GRID_MARGIN; x <= maxX + GRID_MARGIN; x++) {
      for (let y = minY - GRID_MARGIN; y <= maxY + GRID_MARGIN; y++) {
        const key = `${x},${y}`
        const [w, s, e, n] = tileToBounds(x, y, this.gridZoom)
        features.push({
          type: 'Feature',
          properties: { key, selected: this.selectedGridTiles.has(key) },
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [w, s],
                [e, s],
                [e, n],
                [w, n],
                [w, s],
              ],
            ],
          },
        })
      }
    }
    return { type: 'FeatureCollection', features }
  }

  /**
   * Build download plans for every enabled layer. We handle simple raster
   * base layers and raster/WMS overlays whose URL template we can construct
   * directly. Composite layers (which multiplex over N dates) are out of
   * scope for v1 — they'll be cached as a side-effect when the user views
   * the area with those overlays active.
   */
  private async buildLayerPlans(): Promise<void> {
    if (!this.snappedBbox) return

    const prefs = loadLayerPreferences()
    const plans: LayerPlan[] = []

    // --- Base layer -------------------------------------------------
    const baseId = prefs?.baseLayer ?? baseLayers[0].id
    const base = baseLayers.find(l => l.id === baseId)
    if (base) {
      if (base.type === 'vector') {
        await this.buildVectorPlans(base, plans)
      } else {
        const plan = this.buildRasterPlan(base.id, base.name, base, base.options.maxZoom)
        if (plan) plans.push(plan)
      }
    }

    // --- Terrain elevation DEM (needed for hillshade/3D) ------------
    const qualityOption = TERRAIN_QUALITY_OPTIONS.find(o => o.value === this.terrainQuality)!
    const demPlan = this.enumerateTemplatePlan(
      'terrarium-dem',
      'Elevation DEM',
      DEM_URL,
      0,
      Math.min(qualityOption.maxZoom, MAX_DOWNLOAD_ZOOM),
      BYTES_PER_TILE_DEM
    )
    if (demPlan) plans.push(demPlan)

    // --- Selected overlay layers with tile data -----------------------
    for (const overlayId of this.selectedOverlays) {
      const config = PMTILES_OVERLAY_CONFIGS[overlayId]
      if (!config) continue
      const overlay = DOWNLOADABLE_OVERLAYS.find(o => o.id === overlayId)
      if (!overlay) continue
      const plan = this.enumeratePMTilesPlan(
        config.sourceId,
        overlay.name,
        config.minZoom,
        Math.min(config.maxZoom, MAX_DOWNLOAD_ZOOM)
      )
      if (plan) plans.push(plan)
    }

    this.applyPlans(plans)
    void this.refineSizeEstimates()
  }

  private buildRasterPlan(
    id: string,
    name: string,
    layer: BaseLayer | OverlayLayer,
    maxZoom: number | undefined
  ): LayerPlan | null {
    if (!layer.url.includes('{z}') && !layer.url.includes('{bbox-epsg-3857}')) {
      return null
    }
    return this.enumerateTemplatePlan(
      id,
      name,
      layer.url,
      0,
      Math.min(maxZoom ?? MAX_DOWNLOAD_ZOOM, MAX_DOWNLOAD_ZOOM),
      BYTES_PER_TILE_RASTER
    )
  }

  private async buildVectorPlans(base: BaseLayer, plans: LayerPlan[]): Promise<void> {
    if (!this.snappedBbox) return

    const styleBlob = await tileCacheService.getResource(`style-json/${base.url}`, base.url)
    if (!styleBlob) return
    const style = JSON.parse(await styleBlob.text()) as import('maplibre-gl').StyleSpecification

    for (const [name, source] of Object.entries(style.sources ?? {})) {
      const src = source as Record<string, unknown>

      if (src.type === 'vector' && typeof src.url === 'string') {
        const template = await vectorStyleCacheService.resolveTileTemplate(src.url)
        if (!template) continue
        const tj = await vectorStyleCacheService.getTileJson(src.url)
        const maxZoom = tj?.maxzoom ?? 14
        const sourceId = `ofm-vector-${name}`
        const plan = this.enumerateTemplatePlan(
          sourceId,
          `${base.name} (${name})`,
          template,
          0,
          Math.min(maxZoom, MAX_DOWNLOAD_ZOOM),
          BYTES_PER_TILE_VECTOR_PBF
        )
        if (plan) plans.push(plan)
      } else if (src.type === 'raster' && Array.isArray(src.tiles)) {
        const tiles = src.tiles as string[]
        if (tiles.length === 0) continue
        const maxZoom = (src.maxzoom as number) ?? 5
        const sourceId = `ofm-raster-${name}`
        const plan = this.enumerateTemplatePlan(
          sourceId,
          `${base.name} (${name})`,
          tiles[0],
          0,
          Math.min(maxZoom, MAX_DOWNLOAD_ZOOM),
          BYTES_PER_TILE_RASTER
        )
        if (plan) plans.push(plan)
      }
    }
  }

  private applyPlans(plans: LayerPlan[]): void {
    this.layerPlans = plans
    this.totalTiles = plans.reduce((sum, p) => sum + p.tileCount, 0)
    this.estimatedBytes = plans.reduce((sum, p) => sum + p.tileCount * p.bytesPerTile, 0)
  }

  private enumerateTemplatePlan(
    id: string,
    name: string,
    urlTemplate: string,
    zMinFloor: number,
    zMax: number,
    bytesPerTile: number
  ): LayerPlan | null {
    if (!this.snappedBbox) return null
    const zMin = zMinFloor
    if (zMin > zMax) return null

    let count = 0
    for (let z = zMin; z <= zMax; z++) {
      const { minX, minY, maxX, maxY } = bboxToTileRange(this.snappedBbox, z)
      count += (maxX - minX + 1) * (maxY - minY + 1)
    }
    return { id, name, urlTemplate, minZoom: zMin, maxZoom: zMax, tileCount: count, bytesPerTile }
  }

  private enumeratePMTilesPlan(
    sourceId: string,
    name: string,
    zMin: number,
    zMax: number
  ): LayerPlan | null {
    if (!this.snappedBbox) return null
    if (zMin > zMax) return null

    let count = 0
    for (let z = zMin; z <= zMax; z++) {
      const { minX, minY, maxX, maxY } = bboxToTileRange(this.snappedBbox, z)
      count += (maxX - minX + 1) * (maxY - minY + 1)
    }
    return {
      id: sourceId,
      name,
      urlTemplate: '',
      minZoom: zMin,
      maxZoom: zMax,
      tileCount: count,
      bytesPerTile: BYTES_PER_TILE_VECTOR_PBF,
    }
  }

  /**
   * Fetch a few real tiles at max zoom per layer to replace the static
   * bytesPerTile guesses with measured values.
   */
  private async refineSizeEstimates(): Promise<void> {
    if (!this.snappedBbox || this.layerPlans.length === 0) return

    let changed = false
    for (const plan of this.layerPlans) {
      const sampled = await this.sampleLayerTileSize(plan)
      log.info(
        `[estimate] ${plan.name}: sampled=${sampled ?? 'FAILED'} B/tile, ` +
          `static=${plan.bytesPerTile} B/tile, tiles=${plan.tileCount}`
      )
      if (sampled !== null && sampled !== plan.bytesPerTile) {
        plan.bytesPerTile = sampled
        changed = true
      }
    }

    if (changed) {
      this.totalTiles = this.layerPlans.reduce((sum, p) => sum + p.tileCount, 0)
      this.estimatedBytes = this.layerPlans.reduce(
        (sum, p) => sum + p.tileCount * p.bytesPerTile,
        0
      )
      this.requestUpdate()
    }
  }

  private async sampleLayerTileSize(plan: LayerPlan): Promise<number | null> {
    if (!this.snappedBbox) return null

    const { minX, minY, maxX, maxY } = bboxToTileRange(this.snappedBbox, plan.maxZoom)
    const cx = Math.floor((minX + maxX) / 2)
    const cy = Math.floor((minY + maxY) / 2)
    const coords = [
      { z: plan.maxZoom, x: cx, y: cy },
      { z: plan.maxZoom, x: Math.min(cx + 1, maxX), y: cy },
      { z: plan.maxZoom, x: cx, y: Math.min(cy + 1, maxY) },
    ]

    const sizes: number[] = []

    if (plan.urlTemplate) {
      for (const { z, x, y } of coords) {
        try {
          const url = substituteTileTemplate(plan.urlTemplate, z, x, y)
          const resp = await fetch(url)
          if (resp.ok) {
            const blob = await resp.blob()
            sizes.push(blob.size)
          }
        } catch {
          // skip
        }
        if (sizes.length >= 2) break
      }
    } else {
      const config = Object.values(PMTILES_OVERLAY_CONFIGS).find(c => c.sourceId === plan.id)
      if (!config) return null
      const pm = new PMTiles(config.pmtilesUrl)
      for (const { z, x, y } of coords) {
        try {
          const resp = await pm.getZxy(z, x, y)
          if (resp?.data) sizes.push(resp.data.byteLength)
        } catch {
          // skip
        }
        if (sizes.length >= 2) break
      }
    }

    if (sizes.length === 0) return null
    return Math.round(sizes.reduce((a, b) => a + b, 0) / sizes.length)
  }

  // ──────────────────────────────────────────────────────────────────
  // Map init — show the extent for user reference
  // ──────────────────────────────────────────────────────────────────

  private initMap(): void {
    if (this.map || !this.mapRef.value || !this.userBbox || !this.snappedBbox) return

    const prefs = loadLayerPreferences()
    const base =
      baseLayers.find(l => l.id === prefs?.baseLayer) ??
      baseLayers.find(l => l.type !== 'vector') ??
      baseLayers[0]

    const emptyStyle: maplibregl.StyleSpecification = {
      version: 8,
      sources: {},
      layers: [],
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    }

    const [west, south, east, north] = this.snappedBbox

    this.map = new maplibregl.Map({
      container: this.mapRef.value,
      style: emptyStyle,
      bounds: [
        [west, south],
        [east, north],
      ],
      fitBoundsOptions: { padding: 40 },
      attributionControl: { compact: true },
    })

    this.map.on('load', () => {
      if (!this.map) return
      if (base.type === 'vector') {
        vectorStyleCacheService
          .loadStyle(base.url)
          .then(style => {
            this.map?.setStyle(style)
            this.map?.once('styledata', () => this.drawGrid())
          })
          .catch(() => {
            this.drawGrid()
          })
      } else {
        this.map.addSource('base', {
          type: 'raster',
          tiles: [cairnifyTileUrl(base.id, base.url)],
          tileSize: base.options.tileSize ?? 256,
        })
        this.map.addLayer({ id: 'base', type: 'raster', source: 'base' })
        this.drawGrid()
      }
    })
  }

  private drawGrid(): void {
    if (!this.map || !this.userBbox) return

    const userPoly = bboxPolygon(this.userBbox)
    this.map.addSource('user-bbox', { type: 'geojson', data: userPoly })
    this.map.addLayer({
      id: 'user-bbox-line',
      type: 'line',
      source: 'user-bbox',
      paint: { 'line-color': '#3b82f6', 'line-width': 2, 'line-dasharray': [4, 4] },
    })

    this.map.addSource('grid', { type: 'geojson', data: this.buildGridGeoJSON() })
    this.map.addLayer({
      id: 'grid-fill',
      type: 'fill',
      source: 'grid',
      paint: {
        'fill-color': ['case', ['get', 'selected'], '#f97316', '#94a3b8'],
        'fill-opacity': ['case', ['get', 'selected'], 0.2, 0.05],
      },
    })
    this.map.addLayer({
      id: 'grid-line',
      type: 'line',
      source: 'grid',
      paint: {
        'line-color': ['case', ['get', 'selected'], '#f97316', '#94a3b8'],
        'line-width': ['case', ['get', 'selected'], 2.5, 1],
        'line-opacity': ['case', ['get', 'selected'], 1, 0.4],
      },
    })

    this.map.on('click', (e: maplibregl.MapMouseEvent) => this.handleGridClick(e))
  }

  private updateGridOnMap(): void {
    if (!this.map) return
    const source = this.map.getSource('grid')
    if (source && 'setData' in source) {
      ;(source as maplibregl.GeoJSONSource).setData(this.buildGridGeoJSON())
    }
  }

  private handleGridClick(e: maplibregl.MapMouseEvent): void {
    if (this.isDownloading) return
    const { lng, lat } = e.lngLat
    const n = Math.pow(2, this.gridZoom)
    const x = Math.floor(((lng + 180) / 360) * n)
    const latRad = (lat * Math.PI) / 180
    const y = Math.floor(
      ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
    )
    const key = `${x},${y}`

    const next = new Set(this.selectedGridTiles)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    if (next.size === 0) return

    this.selectedGridTiles = next
    this.snappedBbox = this.selectedTilesToBbox()
    this.updateGridOnMap()
    void this.buildLayerPlans()
  }

  // ──────────────────────────────────────────────────────────────────
  // Download execution
  // ──────────────────────────────────────────────────────────────────

  private async startDownload(): Promise<void> {
    if (!this.snappedBbox || this.layerPlans.length === 0) return

    this.errorMessage = ''

    const fit = await tileCacheService.canFit(this.estimatedBytes)
    if (!fit.ok) {
      const availMb = (fit.availableBytes / (1024 * 1024)).toFixed(0)
      const wantMb = (this.estimatedBytes / (1024 * 1024)).toFixed(0)
      this.errorMessage = `Not enough storage. Need ~${wantMb} MB, only ~${availMb} MB available.`
      return
    }

    this.isDownloading = true
    this.progressDone = 0
    this.progressTotal = this.totalTiles
    this.abortController = new AbortController()
    const signal = this.abortController.signal

    let overallDone = 0
    let totalOk = 0
    let totalFailed = 0
    let totalBytes = 0

    const prefs = loadLayerPreferences()
    const baseId = prefs?.baseLayer ?? baseLayers[0].id
    const base = baseLayers.find(l => l.id === baseId)

    try {
      if (base?.type === 'vector') {
        this.currentLayerName = 'Style resources'
        await vectorStyleCacheService.pinStyleResources(base.url, signal)
        log.info('[download] Style resources pinned (not counted in tile estimate)')
      }

      for (const plan of this.layerPlans) {
        if (signal.aborted) break
        this.currentLayerName = plan.name

        const tiles = this.enumerateTiles(plan)

        const estimatedLayerBytes = plan.tileCount * plan.bytesPerTile
        let result: { ok: number; failed: number; bytes: number }

        if (plan.urlTemplate) {
          const urlBuilder = (z: number, x: number, y: number): string =>
            substituteTileTemplate(plan.urlTemplate, z, x, y)

          result = await tileCacheService.pinTiles(
            plan.id,
            tiles,
            urlBuilder,
            (done, _total) => {
              this.progressDone = overallDone + done
            },
            signal
          )
        } else {
          result = await this.downloadPMTilesTiles(plan, tiles, overallDone, signal)
        }

        overallDone += tiles.length
        totalOk += result.ok
        totalFailed += result.failed
        totalBytes += result.bytes

        const actualAvg = result.ok > 0 ? Math.round(result.bytes / result.ok) : 0
        log.info(
          `[download] ${plan.name}: ` +
            `estimated=${(estimatedLayerBytes / (1024 * 1024)).toFixed(1)}MB (${plan.bytesPerTile} B/tile), ` +
            `actual=${(result.bytes / (1024 * 1024)).toFixed(1)}MB (${actualAvg} B/tile avg), ` +
            `tiles=${result.ok}ok/${result.failed}fail`
        )
      }
      if (this.selectedOverlays.size > 0 && !signal.aborted) {
        await this.downloadOverlayData(signal)
      }
    } catch (err) {
      log.warn('download error', err)
      this.errorMessage = `Download failed: ${(err as Error).message}`
    } finally {
      this.isDownloading = false
      this.currentLayerName = ''
    }

    if (!signal.aborted && !this.errorMessage) {
      log.info(
        `[download] TOTAL: estimated=${(this.estimatedBytes / (1024 * 1024)).toFixed(1)}MB, ` +
          `actual=${(totalBytes / (1024 * 1024)).toFixed(1)}MB, ` +
          `ratio=${(totalBytes / Math.max(this.estimatedBytes, 1)).toFixed(2)}x`
      )
      const sourceIds = this.layerPlans.map(p => p.id)
      const bb = this.snappedBbox
      await mapStorage.createMapRegion({
        name: this.planName,
        bounds: {
          ne: [bb[3], bb[2]],
          sw: [bb[1], bb[0]],
        },
        zoomLevel: Math.max(...this.layerPlans.map(p => p.maxZoom)),
        tileCount: totalOk,
        size: totalBytes,
        ttlDays: this.ttlDays,
        sourceIds,
      })

      log.info(
        `download complete: ${totalOk} tiles pinned, ${totalFailed} failed, ${(totalBytes / (1024 * 1024)).toFixed(1)} MB`
      )
      router.navigate(`/plans/${this.planId}`)
    }
  }

  private async downloadPMTilesTiles(
    plan: LayerPlan,
    tiles: TileCoord[],
    overallDone: number,
    signal: AbortSignal
  ): Promise<{ ok: number; failed: number; bytes: number }> {
    const config = Object.values(PMTILES_OVERLAY_CONFIGS).find(c => c.sourceId === plan.id)
    if (!config) return { ok: 0, failed: 0, bytes: 0 }

    const pmtiles = new PMTiles(config.pmtilesUrl)
    let ok = 0
    let failed = 0
    let bytes = 0

    for (let i = 0; i < tiles.length; i++) {
      if (signal.aborted) break
      const { z, x, y } = tiles[i]
      try {
        const resp = await pmtiles.getZxy(z, x, y)
        if (resp?.data) {
          const stored = await tileCacheService.putTileBlob(
            plan.id,
            z,
            x,
            y,
            resp.data,
            'application/x-protobuf'
          )
          ok++
          bytes += stored
        } else {
          failed++
        }
      } catch {
        failed++
      }
      this.progressDone = overallDone + i + 1
    }
    return { ok, failed, bytes }
  }

  private enumerateTiles(plan: LayerPlan): TileCoord[] {
    if (!this.snappedBbox) return []
    const tiles: TileCoord[] = []
    for (let z = plan.minZoom; z <= plan.maxZoom; z++) {
      const { minX, minY, maxX, maxY } = bboxToTileRange(this.snappedBbox, z)
      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          tiles.push({ z, x, y })
        }
      }
    }
    return tiles
  }

  private cancelDownload(): void {
    this.abortController?.abort()
  }

  private onTerrainQualityChange(e: Event): void {
    this.terrainQuality = (e.target as HTMLSelectElement).value as TerrainQuality
    void this.buildLayerPlans()
  }

  private onTtlChange(e: Event): void {
    this.ttlDays = parseInt((e.target as HTMLSelectElement).value, 10)
  }

  // ──────────────────────────────────────────────────────────────────
  // Overlay layer selection
  // ──────────────────────────────────────────────────────────────────

  private initSelectedOverlays(prefs: LayerPreferences | null): void {
    const selected = new Set<string>()
    if (!prefs) {
      this.selectedOverlays = selected
      return
    }
    const t = prefs.terrainOverlays
    for (const overlay of DOWNLOADABLE_OVERLAYS) {
      if (overlay.prefKeys.length === 0) continue
      const isEnabled = overlay.prefKeys.some(key => {
        const val = t[key]
        if (typeof val === 'boolean') return val
        if (typeof val === 'string') {
          try {
            const parsed = JSON.parse(val)
            return Array.isArray(parsed) && parsed.length > 0
          } catch {
            return val.length > 0
          }
        }
        return false
      })
      if (isEnabled) selected.add(overlay.id)
    }
    this.selectedOverlays = selected
  }

  private clickOverlay(id: string): void {
    const next = new Set(this.selectedOverlays)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    this.selectedOverlays = next
    void this.buildLayerPlans()
  }

  private removeOverlay(id: string): void {
    const next = new Set(this.selectedOverlays)
    next.delete(id)
    this.selectedOverlays = next
    void this.buildLayerPlans()
  }

  private onDocumentClick = (e: MouseEvent): void => {
    if (!this.overlayMenuOpen) return
    const path = e.composedPath()
    const dropdown = this.renderRoot.querySelector('.overlay-dropdown')
    if (dropdown && !path.includes(dropdown)) {
      this.overlayMenuOpen = false
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Overlay data pre-fetch (runs after tile downloads)
  // ──────────────────────────────────────────────────────────────────

  private async downloadOverlayData(signal: AbortSignal): Promise<void> {
    if (!this.snappedBbox) return
    const [west, south, east, north] = this.snappedBbox

    for (const overlayId of this.selectedOverlays) {
      if (signal.aborted) return
      const overlay = DOWNLOADABLE_OVERLAYS.find(o => o.id === overlayId)
      if (!overlay) continue
      this.currentLayerName = overlay.name

      try {
        switch (overlayId) {
          case 'avalanche-zones':
            await this.prefetchAvalancheData()
            break
          case 'fire-group':
            await this.prefetchFireData()
            break
          case 'emergency-group':
            await this.prefetchEmergencyData(south, west, north, east)
            break
          case 'wildlife-nps-alerts':
            await this.prefetchNpsAlerts()
            break
        }
      } catch (err) {
        log.warn(`overlay prefetch failed for ${overlayId}`, err)
      }
    }
  }

  private async prefetchAvalancheData(): Promise<void> {
    const now = new Date()
    for (let offset = 1; offset >= -3; offset--) {
      const d = new Date(now)
      d.setDate(d.getDate() + offset)
      const date = d.toISOString().slice(0, 10)
      try {
        await fetch(`/data/avalanche/${date}.geojson`)
      } catch {
        // offline or unavailable — skip
      }
    }
  }

  private async prefetchFireData(): Promise<void> {
    const files = ['wfigs-fires.geojson', 'firms-hotspots.geojson', 'usfws-restrictions.geojson']
    for (const file of files) {
      try {
        await fetch(`/data/fire/${file}`)
      } catch {
        // skip
      }
    }
  }

  private async prefetchEmergencyData(
    south: number,
    west: number,
    north: number,
    east: number
  ): Promise<void> {
    const types: EmergencyDataType[] = [
      'hospitals',
      'fire-stations',
      'law-enforcement',
      'usfs-offices',
    ]
    for (const dataType of types) {
      await emergencyDataService.queryByBounds(dataType, south, west, north, east)
    }
  }

  private async prefetchNpsAlerts(): Promise<void> {
    try {
      await fetch('/data/nps/alerts.json')
    } catch {
      // skip
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────

  override render(): unknown {
    const mb = (this.estimatedBytes / (1024 * 1024)).toFixed(0)
    const progressPct =
      this.progressTotal > 0 ? Math.round((this.progressDone / this.progressTotal) * 100) : 0

    return html`
      <div class="map-container">
        <div class="map-el" ${ref(this.mapRef)}></div>
      </div>

      <div class="action-bar">
        ${this.errorMessage ? html`<div class="error">${this.errorMessage}</div>` : ''}
        ${this.userBbox
          ? html`
              <div class="select-row">
                <md-outlined-select
                  label="Terrain quality"
                  .value=${this.terrainQuality}
                  @change=${(e: Event): void => this.onTerrainQualityChange(e)}
                  ?disabled=${this.isDownloading}
                >
                  ${TERRAIN_QUALITY_OPTIONS.map(
                    o => html`
                      <md-select-option value=${o.value}>
                        <div slot="headline">${o.label} — ${o.subtitle}</div>
                      </md-select-option>
                    `
                  )}
                </md-outlined-select>
              </div>
              <div class="select-row">
                <md-outlined-select
                  label="Keep downloaded maps for"
                  .value=${String(this.ttlDays)}
                  @change=${(e: Event): void => this.onTtlChange(e)}
                  ?disabled=${this.isDownloading}
                >
                  <md-select-option value="7"><div slot="headline">7 days</div></md-select-option>
                  <md-select-option value="30"><div slot="headline">30 days</div></md-select-option>
                  <md-select-option value="90"><div slot="headline">90 days</div></md-select-option>
                  <md-select-option value="0"><div slot="headline">Forever</div></md-select-option>
                </md-outlined-select>
              </div>
              <div class="overlay-section">
                <div class="overlay-dropdown">
                  <button
                    class="overlay-trigger"
                    aria-expanded=${this.overlayMenuOpen}
                    @click=${(): void => {
                      if (!this.isDownloading) this.overlayMenuOpen = !this.overlayMenuOpen
                    }}
                    ?disabled=${this.isDownloading}
                  >
                    <span
                      >Overlay
                      data${this.selectedOverlays.size > 0
                        ? ` (${this.selectedOverlays.size})`
                        : ''}</span
                    >
                    <span class="material-icons">expand_more</span>
                  </button>
                  ${this.overlayMenuOpen
                    ? html`
                        <div class="overlay-menu">
                          ${OVERLAY_GROUPS.map(group => {
                            const layers = DOWNLOADABLE_OVERLAYS.filter(o => o.group === group)
                            return html`
                              <div class="overlay-group-name">${OVERLAY_GROUP_LABELS[group]}</div>
                              ${layers.map(
                                o => html`
                                  <div
                                    class="overlay-item"
                                    @click=${(): void => this.clickOverlay(o.id)}
                                  >
                                    <md-checkbox
                                      ?checked=${this.selectedOverlays.has(o.id)}
                                      touch-target="wrapper"
                                    ></md-checkbox>
                                    <span>${o.name}</span>
                                  </div>
                                `
                              )}
                            `
                          })}
                        </div>
                      `
                    : ''}
                </div>
                ${this.selectedOverlays.size > 0
                  ? html`
                      <div class="overlay-chips">
                        ${[...this.selectedOverlays].map(id => {
                          const overlay = DOWNLOADABLE_OVERLAYS.find(o => o.id === id)
                          if (!overlay) return ''
                          return html`
                            <span class="overlay-chip">
                              ${overlay.name}
                              <button
                                @click=${(): void => this.removeOverlay(id)}
                                aria-label="Remove ${overlay.name}"
                                ?disabled=${this.isDownloading}
                              >
                                <span class="material-icons">close</span>
                              </button>
                            </span>
                          `
                        })}
                      </div>
                    `
                  : ''}
              </div>
              <div class="estimate">
                <div>
                  <div class="estimate-primary">
                    ~${this.totalTiles.toLocaleString()} tiles · ~${mb} MB
                  </div>
                  <div class="estimate-secondary">
                    ${this.layerPlans.length}
                    layer${this.layerPlans.length === 1 ? '' : 's'}${this.selectedOverlays.size > 0
                      ? ` + ${this.selectedOverlays.size} overlay${this.selectedOverlays.size === 1 ? '' : 's'}`
                      : ''}
                  </div>
                </div>
              </div>
              <div class="layer-list">
                ${this.layerPlans.map(
                  p => html`
                    <div class="layer-row">
                      <span>${p.name}</span>
                      <span>
                        ${p.tileCount.toLocaleString()} tiles ·
                        ~${((p.tileCount * p.bytesPerTile) / (1024 * 1024)).toFixed(0)} MB
                      </span>
                    </div>
                  `
                )}
              </div>
            `
          : ''}
        ${this.isDownloading
          ? html`
              <div class="progress-section">
                <div class="progress-label">
                  ${this.currentLayerName
                    ? `${this.currentLayerName} · `
                    : ''}${this.progressDone.toLocaleString()}
                  / ${this.progressTotal.toLocaleString()} (${progressPct}%)
                </div>
                <md-linear-progress .progress=${progressPct / 100}></md-linear-progress>
                <div class="button-row">
                  <md-outlined-button @click=${(): void => this.cancelDownload()}>
                    Cancel
                  </md-outlined-button>
                </div>
              </div>
            `
          : html`
              <div class="button-row">
                <md-filled-button
                  ?disabled=${this.layerPlans.length === 0}
                  @click=${(): void => void this.startDownload()}
                >
                  Download
                </md-filled-button>
              </div>
            `}
      </div>
    `
  }
}

// ────────────────────────────────────────────────────────────────────
// Small pure helper — build a GeoJSON polygon from a bbox
// ────────────────────────────────────────────────────────────────────

function tileToBounds(x: number, y: number, z: number): [number, number, number, number] {
  const n = Math.pow(2, z)
  const west = (x / n) * 360 - 180
  const east = ((x + 1) / n) * 360 - 180
  const toLat = (ty: number): number => {
    const r = Math.PI - (2 * Math.PI * ty) / n
    return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(r) - Math.exp(-r)))
  }
  return [west, toLat(y + 1), east, toLat(y)]
}

function bboxPolygon(bbox: [number, number, number, number]): GeoJSON.Feature {
  const [w, s, e, n] = bbox
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [w, s],
          [e, s],
          [e, n],
          [w, n],
          [w, s],
        ],
      ],
    },
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plan-download': PlanDownload
  }
}
